// Records the live stage exactly as shown: every camera tile + the logo watermark, mixed with everyone's audio.
const TYPES = ["video/mp4;codecs=avc1,mp4a.40.2", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];

function cover(ctx, el, ew, eh, x, y, w, h, fit = "cover") {
  const sc = fit === "cover" ? Math.max(w / ew, h / eh) : Math.min(w / ew, h / eh);
  ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  ctx.drawImage(el, x + w / 2 - (ew * sc) / 2, y + h / 2 - (eh * sc) / 2, ew * sc, eh * sc); ctx.restore();
}

export function startStageRecording(getStreams, { width = 1280, height = 720, logo = "/brand/logo-full.png", maxMs = 3 * 60 * 60 * 1000, getScene = null, mediaUrl = (u) => u } = {}) {
  if (!window.MediaRecorder || !HTMLCanvasElement.prototype.captureStream) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  const img = new Image(); img.src = logo;
  const vids = new Map();
  const ac = new AudioContext();
  const dest = ac.createMediaStreamDestination();
  ac.resume().catch(() => {});
  const wired = new Set();
  const mark = new Image(); mark.src = "/brand/logo-mark.png";
  const mv = document.createElement("video");
  mv.crossOrigin = "anonymous"; mv.playsInline = true; mv.preload = "auto";
  let mvUrl = "", mvOk = false, mvWired = false;
  mv.onloadeddata = () => { mvOk = true; };
  mv.onerror = () => { mvOk = false; };
  const drawScene = (sc) => {
    const m = sc?.media;
    if (m && m.kind === "video") {
      const u = mediaUrl(m.url);
      if (u !== mvUrl) {
        mvUrl = u; mvOk = false; mv.src = u;
        const at = Math.max(0, Date.now() / 1000 - (m.started || Date.now() / 1000));
        mv.onloadedmetadata = () => { try { mv.currentTime = Math.min(at, mv.duration || at); } catch { /* noop */ } };
        if (!mvWired) { try { ac.createMediaElementSource(mv).connect(dest); mvWired = true; } catch { /* noop */ } }
        mv.play().catch(() => {});
      }
    } else if (mvUrl) { mv.pause(); mv.removeAttribute("src"); mvUrl = ""; mvOk = false; }
    if (m) {
      const full = m.mode === "full";
      const w = full ? width : width * 0.38, h = full ? height : w * 9 / 16, x = full ? 0 : width - w - 24, y = full ? 0 : 24;
      ctx.fillStyle = "#000"; ctx.fillRect(x, y, w, h);
      if (m.kind === "video" && mvOk && mv.videoWidth) cover(ctx, mv, mv.videoWidth, mv.videoHeight, x, y, w, h, "contain");
      else { ctx.fillStyle = "#c9a25a"; ctx.font = `${Math.round(h / 12)}px Georgia, serif`; ctx.textAlign = "center"; ctx.fillText(`Now playing: ${m.title || "video"}`, x + w / 2, y + h / 2); ctx.textAlign = "start"; }
      if (!full) { ctx.strokeStyle = "#c9a25a"; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h); }
    }
    if (sc?.overlay) {
      ctx.fillStyle = "#0d0b09"; ctx.fillRect(0, 0, width, height);
      if (mark.complete && mark.naturalWidth) { const s = height * 0.22; ctx.drawImage(mark, width / 2 - s / 2, height * 0.22, s, s * mark.naturalHeight / mark.naturalWidth); }
      ctx.textAlign = "center"; ctx.fillStyle = "#c9a25a"; ctx.font = `${Math.round(height / 11)}px Georgia, serif`;
      ctx.fillText(sc.overlay === "break" ? "Commercial break" : "Be right back", width / 2, height * 0.66);
      ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = `${Math.round(height / 32)}px sans-serif`;
      ctx.fillText(sc.overlay === "break" ? "Stay with us. The show continues in a moment." : "We'll be right back. Don't go anywhere!", width / 2, height * 0.74);
      ctx.textAlign = "start";
    }
    if (sc?.news) drawNews(sc.news);
  };
  // News graphics: LIVE bug + clock, name lower third, scrolling ticker.
  const drawNews = (n) => {
    const tk = (n.ticker || "").split("\n").map((x) => x.trim()).filter(Boolean).join("   •   ");
    const th = 40;
    if (n.bug) {
      ctx.font = "bold 18px sans-serif"; ctx.fillStyle = "#dc2626"; ctx.fillRect(24, 24, 70, 32); ctx.fillStyle = "#fff"; ctx.fillText("LIVE", 36, 47);
      ctx.fillStyle = "rgba(0,0,0,0.7)"; ctx.fillRect(98, 24, 96, 32); ctx.fillStyle = "#fff"; ctx.font = "18px sans-serif";
      ctx.fillText(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), 106, 47);
      if (mark.complete && mark.naturalWidth) ctx.drawImage(mark, 200, 22, 36, 36);
    }
    if (n.lower?.name) {
      const y = height - (tk ? th : 0) - 84;
      ctx.font = "30px Georgia, serif"; const w = Math.max(ctx.measureText(n.lower.name).width, n.lower.title ? n.lower.title.length * 11 : 0) + 48;
      ctx.fillStyle = "#c9a25a"; ctx.fillRect(0, y, 8, 74); ctx.fillStyle = "rgba(0,0,0,0.8)"; ctx.fillRect(8, y, w, 74);
      ctx.fillStyle = "#fff"; ctx.fillText(n.lower.name, 28, y + 36);
      if (n.lower.title) { ctx.fillStyle = "#c9a25a"; ctx.font = "15px sans-serif"; ctx.fillText(n.lower.title.toUpperCase(), 28, y + 60); }
    }
    if (tk) {
      ctx.fillStyle = "rgba(13,11,9,0.95)"; ctx.fillRect(0, height - th, width, th);
      ctx.font = "19px sans-serif"; const tw = ctx.measureText(tk).width, x = width - ((Date.now() / 1000 * 110) % (tw + width));
      ctx.fillStyle = "#fff"; ctx.fillText(tk, x, height - 13);
      ctx.fillStyle = "#c9a25a"; ctx.fillRect(0, height - th, 140, th); ctx.fillStyle = "#000"; ctx.font = "bold 15px sans-serif"; ctx.fillText("HEADLINES", 22, height - 14);
    }
  };
  let raf;
  const draw = () => {
    const streams = getStreams().filter((s) => s?.getVideoTracks().length);
    for (const s of getStreams()) {
      if (s && !wired.has(s.id) && s.getAudioTracks().length) { try { ac.createMediaStreamSource(s).connect(dest); wired.add(s.id); } catch { /* noop */ } }
    }
    ctx.fillStyle = "#0b0b0b"; ctx.fillRect(0, 0, width, height);
    const n = Math.max(1, streams.length), cols = n <= 1 ? 1 : n <= 4 ? 2 : 3, rows = Math.ceil(n / cols);
    const cw = width / cols, ch = height / rows;
    streams.forEach((s, i) => {
      let v = vids.get(s.id);
      if (!v) { v = document.createElement("video"); v.muted = true; v.playsInline = true; v.srcObject = s; v.play().catch(() => {}); vids.set(s.id, v); }
      const x = (i % cols) * cw, y = Math.floor(i / cols) * ch;
      const vw = v.videoWidth || 16, vh = v.videoHeight || 9, sc = Math.max(cw / vw, ch / vh);
      ctx.save(); ctx.beginPath(); ctx.rect(x + 2, y + 2, cw - 4, ch - 4); ctx.clip();
      ctx.drawImage(v, x + cw / 2 - (vw * sc) / 2, y + ch / 2 - (vh * sc) / 2, vw * sc, vh * sc); ctx.restore();
    });
    if (getScene) drawScene(getScene());
    if (img.complete && img.naturalWidth) {
      const w = width * 0.16, h = (img.naturalHeight / img.naturalWidth) * w, lift = getScene?.()?.news?.ticker ? 44 : 0;
      ctx.globalAlpha = 0.8; ctx.drawImage(img, width - w - 24, height - h - 20 - lift, w, h); ctx.globalAlpha = 1;
    }
    raf = document.hidden ? setTimeout(draw, 33) : requestAnimationFrame(draw);
  };
  draw();
  const out = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...dest.stream.getAudioTracks()]);
  const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
  const rec = new MediaRecorder(out, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  rec.start(2000);
  const timer = setTimeout(() => rec.state !== "inactive" && rec.stop(), maxMs);
  return {
    stop: () => new Promise((resolve) => {
      clearTimeout(timer);
      const done = () => { cancelAnimationFrame(raf); clearTimeout(raf); ac.close().catch(() => {}); vids.forEach((v) => { v.srcObject = null; }); mv.pause(); mv.removeAttribute("src"); resolve(chunks.length ? new Blob(chunks, { type: (rec.mimeType || "video/webm").split(";")[0] }) : null); };
      if (rec.state === "inactive") done(); else { rec.onstop = done; rec.stop(); }
    }),
  };
}

export async function uploadChunked(url, blob, api, onProgress = () => {}) {
  const id = [...crypto.getRandomValues(new Uint8Array(12))].map((b) => b.toString(16).padStart(2, "0")).join("");
  const size = 5 * 1024 * 1024, total = Math.max(1, Math.ceil(blob.size / size));
  let last;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", id); fd.append("index", String(i)); fd.append("final", String(i === total - 1));
    fd.append("file", new File([blob.slice(i * size, (i + 1) * size)], `rec.${blob.type.includes("mp4") ? "mp4" : "webm"}`, { type: blob.type }));
    last = await api.post(url, fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return last.data;
}
